/**
 * SlideshowParallax — depth-based 2.5D rendering for slides, on the shared
 * depth-engine.
 *
 * What changed and why
 * --------------------
 * This file used to carry its own copy of the depth pipeline and its own
 * two-layer renderer, with constants that had already drifted from the copy in
 * depth-parallax.js (threshold 0.45 vs 0.50, feather 0.15 vs 0.12). It also
 * hard-coded its model URLs, and the fallback pointed at
 * `Faridzar/dfn3-wasm-mirror`, which does not host depth-anything-small-q.onnx
 * — so the moment the primary URL failed, the feature failed with it. And
 * because it bypassed AssetRouter entirely, non-RU visitors were sent to the
 * RU origin first.
 *
 * Both problems are gone: depth comes from depth-engine (one implementation,
 * one set of constants) and the URLs are supplied by the caller, which gets
 * them from AssetRouter.
 *
 * The Ken Burns motion itself is UNCHANGED. The camera path, easing, zoom and
 * anchor still come from SlideshowKenBurns exactly as before; only the way the
 * depth separation is drawn is different. The parallax displacement is derived
 * from the same damped/amplified progress pair the old code used, so a slide
 * moves the way it always did — it just no longer doubles its edges.
 */

import DepthEngine from '../depth-engine/index.js';

const DEPTH_INPUT_SIZE = 384;
const FOCUS = 0.45;            // the old FG_THRESHOLD: which depth stays still
const DEFAULT_STRENGTH = 0.6;

// Fallback chain when the caller supplies nothing. Page-relative first (works
// on RU where the file is vendored), then the REAL mirror. The old literal
// here pointed at dfn3-wasm-mirror and was simply wrong.
const FALLBACK_MODEL_URLS = [
    '/vendor/ai-depth/depth-anything-v2-small-q.onnx',
    'https://huggingface.co/Faridzar/depth-anything-mirror/resolve/main/depth-anything-small-q.onnx',
];

let _modelUrls = null;
let _renderer = null;
let _glCanvas = null;
let _bound = null;
let _boundImg = null;

/** Let the page hand us AssetRouter's heavy()/heavyFallback() pair. */
export function configure(opts) {
    if (opts && opts.modelUrls && opts.modelUrls.length) _modelUrls = opts.modelUrls.slice();
}

function modelUrls() {
    return _modelUrls || FALLBACK_MODEL_URLS;
}

function renderer() {
    if (_renderer && !_renderer.isLost()) return _renderer;
    if (!DepthEngine.isSupported()) return null;
    _glCanvas = document.createElement('canvas');
    _renderer = DepthEngine.createRenderer(_glCanvas);
    _bound = null;
    _boundImg = null;
    return _renderer;
}

function ensureDepthLib() {
    if (DepthEngine.isRuntimeReady()) return Promise.resolve(true);
    return Promise.reject(new Error('Timbrica3D depth API not loaded — include 3d-printing-core.js'));
}

/** Idempotent per-slide depth analysis. Coalesces concurrent calls. */
export async function analyzeSlide(slide, onProgress) {
    if (slide.parallaxState === 'ready') return slide;
    if (slide.parallaxState === 'pending') return slide.__parallaxPromise || slide;
    if (!slide.imageEl && window.SlideshowPhotoCache) await window.SlideshowPhotoCache.ensure(slide);
    if (!slide.imageEl || !slide.imageEl.complete) throw new Error('image-not-ready');

    slide.parallaxState = 'pending';
    slide.__parallaxPromise = (async function () {
        await ensureDepthLib();
        if (onProgress) onProgress({ stage: 'load-model', loaded: 0, total: 1 });

        const map = await DepthEngine.analyze(slide.imageEl, {
            modelUrls: modelUrls(),
            inputSize: DEPTH_INPUT_SIZE,
            onProgress: onProgress,
        });

        slide.depthMapObj = map;
        slide.depthMap = map.data;
        slide.depthDims = { w: map.w, h: map.h };
        slide.parallaxState = 'ready';
        slide.parallaxEnabled = true;
        return slide;
    })().catch(function (err) {
        slide.parallaxState = 'error';
        slide.parallaxError = (err && err.message) || String(err);
        throw err;
    });
    return slide.__parallaxPromise;
}

/** The source rectangle a Ken Burns preset shows at a given progress. */
function kbRect(slide, w, h, progress, userZoom) {
    const tr = window.SlideshowKenBurns.transform(slide.kenBurns || 'zoom-in-center', progress);
    const scale = tr.scale * userZoom;
    const iw = slide.imageEl.naturalWidth, ih = slide.imageEl.naturalHeight;
    const ir = iw / ih, cr = w / h;
    let baseSw, baseSh;
    if (ir > cr) { baseSh = ih; baseSw = Math.round(ih * cr); }
    else { baseSw = iw; baseSh = Math.round(iw / cr); }
    const visW = baseSw / scale, visH = baseSh / scale;
    const sx = Math.max(0, Math.min(iw - visW, tr.anchorX * (iw - visW)));
    const sy = Math.max(0, Math.min(ih - visH, tr.anchorY * (ih - visH)));
    return { sx, sy, visW, visH, iw, ih, scale };
}

export function draw(ctx, slide, w, h, progress, settings, __rotated) {
    if (!slide || slide.parallaxState !== 'ready' || !slide.depthMapObj) return false;

    // A rotated slide turns as a whole, or the layers come apart. Same trick as
    // SlideshowKenBurns.draw: turn the canvas, then run the untouched maths.
    const rot = !__rotated ? (((Math.round((Number(slide.rotate) || 0) / 90) * 90) % 360 + 360) % 360) : 0;
    if (rot === 90 || rot === 180 || rot === 270) {
        const swap = (rot === 90 || rot === 270);
        const fw = swap ? h : w;
        const fh = swap ? w : h;
        ctx.save();
        ctx.translate(w / 2, h / 2);
        ctx.rotate(rot * Math.PI / 180);
        ctx.translate(-fw / 2, -fh / 2);
        const ok = draw(ctx, slide, fw, fh, progress, settings, true);
        ctx.restore();
        return ok;
    }

    const r = renderer();
    if (!r) return false;

    let strength = (slide.parallaxStrength != null)
        ? slide.parallaxStrength
        : (settings && settings.parallaxStrength != null ? settings.parallaxStrength : DEFAULT_STRENGTH);
    strength = Math.max(0, Math.min(1, strength));

    const userZoom = Math.max(1, Math.min(3, Number(slide.zoom) || 1));

    // Exactly the two progress values the old renderer used for its two
    // layers. The difference between the frames they select IS the parallax
    // displacement — so deriving the camera offset from it reproduces the old
    // motion without the old compositing.
    const dampedProgress = progress * (1 - strength * 0.5);
    const ampedProgress = Math.min(1, progress + (1 - progress) * strength * 0.5);

    const base = kbRect(slide, w, h, dampedProgress, userZoom);
    const amped = kbRect(slide, w, h, ampedProgress, userZoom);

    // Rebind when the slide changes OR when its bitmap does: the photo cache
    // can evict and re-decode a photo, and the new <img> is a different object
    // — a texture bound to the old one would keep drawing freed pixels.
    if (_bound !== slide || _boundImg !== slide.imageEl) {
        if (!r.setColor(slide.imageEl)) return false;
        r.setDepth(slide.depthMapObj, { refine: true });
        _bound = slide;
        _boundImg = slide.imageEl;
    }
    r.resize(w, h);

    const ok = r.render({
        offsetUv: {
            x: (amped.sx - base.sx) / base.iw,
            y: (amped.sy - base.sy) / base.ih,
        },
        center: {
            x: (base.sx + base.visW / 2) / base.iw,
            y: (base.sy + base.visH / 2) / base.ih,
        },
        // The renderer cover-fits then divides by overscale; base.scale is
        // already relative to that same cover fit, so it maps straight across.
        zoom: base.scale,
        overscale: 1.0,
        focus: FOCUS,
        steps: 40,
        mode: 'mono',
    });
    if (!ok) return false;
    ctx.drawImage(_glCanvas, 0, 0, w, h);
    return true;
}

export function isAnalyzed(slide) {
    return !!(slide && slide.parallaxState === 'ready' && slide.depthMapObj);
}

window.SlideshowParallax = {
    analyzeSlide: analyzeSlide,
    draw: draw,
    isAnalyzed: isAnalyzed,
    configure: configure,
};

// The page configures our model URLs from an inline (classic, immediately
// executed) script, which necessarily runs BEFORE this deferred module. It
// waits on this event — not on depth-engine:ready, which fires while this
// module's own body is still pending.
try {
    window.dispatchEvent(new CustomEvent('slideshow-parallax:ready'));
} catch (_) {}
